import rybbit from "@rybbit/js";

const config = {
	analyticsHost: "https://app.rybbit.io/api",
	// staging reports to its own Rybbit site so production funnel data stays clean
	siteId: process.env.NODE_ENV === "staging" ? "990ffe48385d" : "1160",
};

// init() is async (it fetches remote config); rybbit.event()/pageview() are
// no-ops until it resolves and do NOT throw, so anything emitted before then is
// silently dropped — which lost the first tour steps on a cold demo start.
// Everything that emits awaits this promise instead of calling rybbit directly.
export const rybbitReady: Promise<void> = rybbit.init(config).then(() => {});

const UTM_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_content",
	"utm_term",
] as const;

const getCookie = (name: string): string | null => {
	const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return m ? decodeURIComponent(m[1]) : null;
};

// Campaign UTMs attached to EVERY Rybbit event (see emitRybbitEvent), so each
// event is attributable to the marketing-site ad click on its own, not only
// via its session's pageview. Read in priority order: live URL → the landing
// query App.tsx saved before the router stripped it (uploadRecord deletes it
// after signup, hence the cache) → the qp_attr cookie the marketing site
// writes on .quickpoint.me (covers return visits with no UTMs on the URL).
// Cached once found; retried while empty so a cookie written mid-session
// (consent granted on the marketing site in another tab) still gets picked up.
let cachedUtms: Record<string, string> | null = null;
const attributionUtms = (): Record<string, string> => {
	if (cachedUtms) return cachedUtms;
	const out: Record<string, string> = {};
	try {
		const sources = [
			new URLSearchParams(window.location.search),
			new URLSearchParams(sessionStorage.getItem("queryString") ?? ""),
		];
		let cookieAttr: Record<string, unknown> = {};
		try {
			cookieAttr = JSON.parse(getCookie("qp_attr") ?? "null") ?? {};
		} catch {
			// malformed cookie — URL/sessionStorage sources still apply
		}
		for (const k of UTM_KEYS) {
			const v =
				sources.map((s) => s.get(k)).find(Boolean) ??
				(typeof cookieAttr[k] === "string" ? (cookieAttr[k] as string) : null);
			if (v) out[k] = v;
		}
	} catch {
		// private mode etc. — events just go out without UTMs
	}
	if (Object.keys(out).length > 0) cachedUtms = out;
	return out;
};

/**
 * Fire a Rybbit custom event once init has resolved. Campaign UTMs from the
 * marketing site are merged into every event's props automatically; explicit
 * props win on collision.
 */
export function emitRybbitEvent(
	name: string,
	props?: Record<string, string | number | boolean>,
): void {
	rybbitReady.then(() => {
		try {
			rybbit.event(name, { ...attributionUtms(), ...props });
		} catch {
			// analytics must never break the app
		}
	});
}

/** Fire a Rybbit pageview once init has resolved. */
export function rybbitPageview(path: string): void {
	rybbitReady.then(() => {
		try {
			rybbit.pageview(path);
		} catch {
			// analytics must never break the app
		}
	});
}
